import { DeviceGroupPolicyVaultNotInitializedError } from '../../errors/device-group-policy-vault/device-group-policy-vault-not-initialized-error';
import { InvalidDateDeviceGroupPolicyVaultEntryError } from '../../errors/device-group-policy-vault/invalid-date-device-group-policy-vault-entry-error';
import { InvalidDeviceGroupPolicyVaultEntryError } from '../../errors/device-group-policy-vault/invalid-device-group-policy-vault-entry-error';
import { InvalidDeviceGroupPolicyVaultEntrySignatureError } from '../../errors/device-group-policy-vault/invalid-device-group-policy-vault-entry-signature-error';
import { addMinutes, areEqualUint8Array, combineUint8Arrays, getUnsafeDateNow } from '../../utils/utils';
import { getDeviceGroupPolicyVaultEntryBytesToBeSigned } from './device-group-policy-utils';
import { DeviceGroupPolicyVaultOperation } from './device-group-policy-vault-operation';
export class DeviceGroupPolicyValidationContext {
    constructor(entries, keyVault, groupVault, allowEmpty, validateFromEntry) {
        this.keyVault = keyVault;
        this.groupVault = groupVault;
        this.allowEmpty = allowEmpty;
        this.validateFromEntry = validateFromEntry;
        this.supportedVaultEntryVersion = 1;
        this.minimumDate = new Date(Date.UTC(2022, 4, 23, 0, 0, 0));
        this.timeSkew = 5; // in minutes
        this.entries = entries;
        this.validatedEntries = entries.slice(0, validateFromEntry);
        this.validatedEffectiveGroupEntries = [];
        this.validatedEffectiveGlobalEntries = [];
        this.validatedEntries.forEach(entry => {
            this.updateEffectiveEntries(entry);
        });
    }
    async validateAll() {
        if (this.entries.length === 0 && !this.allowEmpty) {
            throw new DeviceGroupPolicyVaultNotInitializedError();
        }
        for (const entry of this.entries.slice(this.validateFromEntry)) {
            await this.validate(entry);
        }
    }
    async validate(entry) {
        this.validateEntry(entry);
        this.validateOperation(entry);
        if (entry.entryId === 0) {
            this.validateRootEntry(entry);
        }
        else {
            this.validateNonRootEntry(entry);
        }
        await this.verifyEntrySignature(entry);
        this.validatedEntries.push(entry);
        this.updateEffectiveEntries(entry);
    }
    updateEffectiveEntries(entry) {
        if (entry.operation === DeviceGroupPolicyVaultOperation.RemovePolicy) {
            this.validatedEffectiveGroupEntries = this.validatedEffectiveGroupEntries.filter(e => !(e.policyId === entry.policyId && e.groupId === entry.groupId));
        }
        if (entry.operation === DeviceGroupPolicyVaultOperation.RemoveGlobalPolicy) {
            this.validatedEffectiveGlobalEntries = this.validatedEffectiveGlobalEntries.filter(e => e.policyId !== entry.policyId);
        }
        if (entry.operation === DeviceGroupPolicyVaultOperation.AddPolicy) {
            this.validatedEffectiveGroupEntries.push({ groupId: entry.groupId, policyId: entry.policyId, policyHash: entry.policyHash, policyType: entry.policyType });
        }
        if (entry.operation === DeviceGroupPolicyVaultOperation.AddGlobalPolicy) {
            this.validatedEffectiveGlobalEntries.push({ groupId: null, policyId: entry.policyId, policyHash: entry.policyHash, policyType: entry.policyType });
        }
        if (entry.operation === DeviceGroupPolicyVaultOperation.ReplacePolicy) {
            this.validatedEffectiveGroupEntries = this.applyReplacement(this.validatedEffectiveGroupEntries, entry);
            this.validatedEffectiveGlobalEntries = this.applyReplacement(this.validatedEffectiveGlobalEntries, entry);
        }
    }
    validateEntry(entry) {
        if (entry.version !== this.supportedVaultEntryVersion) {
            throw new InvalidDeviceGroupPolicyVaultEntryError('Unsupported version.', entry);
        }
        if (entry.entryId !== this.validatedEntries.length) {
            throw new InvalidDeviceGroupPolicyVaultEntryError('Entry id mismatch.', entry);
        }
        if (entry.addedAt < this.minimumDate || entry.addedAt > addMinutes(getUnsafeDateNow(), this.timeSkew)) {
            throw new InvalidDateDeviceGroupPolicyVaultEntryError(entry);
        }
    }
    validateOperation(entry) {
        switch (entry.operation) {
            case DeviceGroupPolicyVaultOperation.NoOp:
                break;
            case DeviceGroupPolicyVaultOperation.AddPolicy:
                if (this.validatedEffectiveGroupEntries.some(e => e.groupId === entry.groupId && e.policyId === entry.policyId)) {
                    throw new InvalidDeviceGroupPolicyVaultEntryError('Policy is already in the vault.', entry);
                }
                if (!this.groupVault.effectiveEntriesAt(entry.addedAt).some(e => e.groupId == entry.groupId)) {
                    throw new InvalidDeviceGroupPolicyVaultEntryError('Device group is not effective.', entry);
                }
                break;
            case DeviceGroupPolicyVaultOperation.RemovePolicy:
                if (!this.validatedEffectiveGroupEntries.some(e => e.groupId === entry.groupId && e.policyId === entry.policyId)) {
                    throw new InvalidDeviceGroupPolicyVaultEntryError('Policy is not in the vault.', entry);
                }
                if (!this.groupVault.effectiveEntriesAt(entry.addedAt).some(e => e.groupId == entry.groupId)) {
                    throw new InvalidDeviceGroupPolicyVaultEntryError('Device group is not effective.', entry);
                }
                break;
            case DeviceGroupPolicyVaultOperation.AddGlobalPolicy:
                if (this.validatedEffectiveGlobalEntries.some(e => e.policyId === entry.policyId)) {
                    throw new InvalidDeviceGroupPolicyVaultEntryError('Global policy is already in the vault.', entry);
                }
                break;
            case DeviceGroupPolicyVaultOperation.RemoveGlobalPolicy:
                if (!this.validatedEffectiveGlobalEntries.some(e => e.policyId === entry.policyId)) {
                    throw new InvalidDeviceGroupPolicyVaultEntryError('Global policy is not in the vault.', entry);
                }
                break;
            case DeviceGroupPolicyVaultOperation.ReplacePolicy: {
                const replaceEntry = this.validatedEntries.find(e => e.operation === DeviceGroupPolicyVaultOperation.ReplacePolicy && e.replacedPolicyId === entry.replacedPolicyId);
                if (replaceEntry) {
                    throw new InvalidDeviceGroupPolicyVaultEntryError(`Policy was already replaced with ${replaceEntry.policyId}.`, entry);
                }
                break;
            }
            default:
                throw new InvalidDeviceGroupPolicyVaultEntryError('Unsupported operation.', entry);
        }
    }
    validateRootEntry(entry) {
        if (!areEqualUint8Array(entry.entrySignature, entry.blockchainSignature)) {
            throw new InvalidDeviceGroupPolicyVaultEntrySignatureError('BlockchainSignature', entry);
        }
        if (entry.operation !== DeviceGroupPolicyVaultOperation.NoOp) {
            throw new InvalidDeviceGroupPolicyVaultEntryError(`Root entry must have ${DeviceGroupPolicyVaultOperation.NoOp} operation.`, entry);
        }
    }
    validateNonRootEntry(entry) {
        if (entry.operation === DeviceGroupPolicyVaultOperation.NoOp) {
            throw new InvalidDeviceGroupPolicyVaultEntryError(`Non root entry must not have ${DeviceGroupPolicyVaultOperation.NoOp} operation.`, entry);
        }
        const previousEntry = this.entries[entry.entryId - 1];
        if (previousEntry.addedAt >= entry.addedAt) {
            throw new InvalidDateDeviceGroupPolicyVaultEntryError(entry);
        }
        if (entry.vaultEntryId < 0) {
            throw new InvalidDeviceGroupPolicyVaultEntryError('VaultEntryId is invalid.', entry);
        }
    }
    async verifyEntrySignature(entry) {
        if (entry.entryId !== 0) {
            const previousEntry = this.validatedEntries[entry.entryId - 1];
            const blockchainSignaturePayload = combineUint8Arrays(previousEntry.blockchainSignature, entry.entrySignature);
            await this.keyVault.verifySignatureByIdAt(blockchainSignaturePayload, entry.blockchainSignature, 'BlockchainSignature', entry.addedAt, entry.vaultEntryId);
        }
        const entrySignaturePayload = getDeviceGroupPolicyVaultEntryBytesToBeSigned(entry);
        await this.keyVault.verifySignatureByIdAt(entrySignaturePayload, entry.entrySignature, 'EntrySignature', entry.addedAt, entry.vaultEntryId);
    }
    applyReplacement(entries, replaceEntry) {
        return entries.map(resultEntry => {
            if (resultEntry.policyId !== replaceEntry.replacedPolicyId) {
                return resultEntry;
            }
            return {
                policyId: replaceEntry.policyId,
                policyHash: replaceEntry.policyHash,
                groupId: resultEntry.groupId,
                policyType: resultEntry.policyType
            };
        });
    }
}
